import { FormEvent, useEffect, useState } from 'react';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { StorageAccessKey } from '../../services/storageApi';

export type StorageAccessKeyFormValues = {
  name: string;
  description: string;
  expiresAtUtc: string;
};

type StorageAccessKeyFormModalProps = {
  open: boolean;
  initial?: StorageAccessKey | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: StorageAccessKeyFormValues) => Promise<void>;
};

function toDateInputValue(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export function StorageAccessKeyFormModal({
  open,
  initial,
  saving,
  onClose,
  onSubmit,
}: StorageAccessKeyFormModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expiresAtUtc, setExpiresAtUtc] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setDescription(initial?.description ?? '');
    setExpiresAtUtc(toDateInputValue(initial?.expiresAtUtc));
  }, [open, initial]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await onSubmit({
      name: name.trim(),
      description: description.trim(),
      expiresAtUtc,
    });
  }

  return (
    <DraggableModalPanel
      open={open}
      title={initial ? 'Editar chave de acesso' : 'Nova chave de acesso'}
      subtitle="O secret é armazenado com segurança e pode ser revelado depois."
      onClose={onClose}
      closeDisabled={saving}
      footer={
        <>
          <button
            type="button"
            className="erp-btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="storage-access-key-form"
            className="erp-btn-primary"
            disabled={saving}
          >
            {saving ? 'Salvando...' : initial ? 'Salvar' : 'Criar chave'}
          </button>
        </>
      }
    >
      <form id="storage-access-key-form" className="space-y-3" onSubmit={(e) => void handleSubmit(e)}>
        <label className="block text-sm">
          <span className="mb-1 block text-slate-600">
            Nome <RequiredMark />
          </span>
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Ex.: IntegracaoBackup"
            required
            maxLength={120}
          />
          <span className="mt-1 block text-xs text-slate-500">
            No MinIO: até 32 caracteres, começando com letra (A–Z, 0–9, _ ou -).
          </span>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-slate-600">Descrição</span>
          <textarea
            className="w-full rounded-xl border border-slate-300 px-3 py-2"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Uso previsto da chave"
            maxLength={500}
          />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-slate-600">Expira em (opcional)</span>
          <input
            type="date"
            className="w-full rounded-xl border border-slate-300 px-3 py-2"
            value={expiresAtUtc}
            onChange={(event) => setExpiresAtUtc(event.target.value)}
          />
        </label>
      </form>
    </DraggableModalPanel>
  );
}
